import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchWithLocalCache } from "../lib/cache";

const STOCK_SCREENER_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/stock_screener.json";

const ETF_ORDER = [
  "Nanoscale Dominance",
  "Synthetic Biology Revolution",
  "Boomer Housing Turnover",
  "Democratic Regulatory Upheaval",
  "EM AI Leapfrog",
  "AI Induced Deflation",
  "Downcycle Defensive",
  "Long Shots",
];

const ETF_DESCRIPTIONS = {
  "Nanoscale Dominance":
    "This ETF is built around the idea that the next generation of technological dominance will happen at the nano-scale layer of the economy. As AI accelerates hardware complexity, companies that design, simulate, manufacture, and optimize matter at microscopic scales become increasingly valuable. This includes advanced semiconductor design, photonics, quantum systems, precision manufacturing, advanced materials, and scientific instrumentation. The belief behind this ETF is that future technological bottlenecks will not primarily come from software, but from the physical complexity of the underlying hardware systems. As chips become more customized and industries move toward atomic-scale optimization, the companies enabling this transition could become foundational infrastructure providers for the next industrial era.",
  "Synthetic Biology Revolution":
    "This ETF is based on the belief that biology is gradually becoming programmable infrastructure. Rather than viewing biotech purely as pharmaceutical speculation, this theme focuses on the picks-and-shovels layer powering the industrialization of biology. Advances in gene editing, AI-driven protein modeling, sequencing, synthesis, automation, lab instrumentation, and biological manufacturing could eventually transform healthcare, agriculture, chemicals, materials, and even computing itself. The thesis is that as synthetic biology matures, the companies providing the tools, consumables, sequencing systems, manufacturing infrastructure, and automation platforms may benefit regardless of which individual therapies ultimately succeed. This ETF is designed around the long-term possibility that biology evolves into a scalable engineering discipline similar to software or semiconductor design.",
  "Boomer Housing Turnover":
    "This ETF reflects the view that the United States is approaching a major housing transition driven by demographic turnover and aging housing infrastructure. A large percentage of homes are owned by aging baby boomers, while younger generations continue to face housing shortages, underbuilding, and deferred renovation demand. Over time, housing turnover, remodeling, repairs, retrofits, energy efficiency upgrades, and new construction could create a prolonged demand cycle across building products, home improvement, lumber, coatings, HVAC, insulation, flooring, and housing-related infrastructure. The ETF focuses on companies that may benefit from long-duration housing normalization rather than short-term speculative housing booms. The core idea is that much of the housing stock in America is old, inefficient, and under-maintained, creating structural demand that could persist for years.",
  "Democratic Regulatory Upheaval":
    "This ETF is built around the belief that a Democratic political victory could lead to a significant expansion of government-driven spending, regulation, subsidies, and public-sector programs across healthcare, environmental infrastructure, clean energy, and social support systems. The core thesis is that increased federal involvement in the economy would likely redirect large amounts of capital toward industries aligned with government priorities, including renewable energy, grid modernization, healthcare access, diagnostics, infrastructure upgrades, environmental compliance, public administration, and regulated service providers.",
  "EM AI Leapfrog":
    "This ETF is based on the belief that emerging markets may become some of the largest beneficiaries of AI-driven productivity growth. Many emerging economies are not burdened by the same legacy infrastructure, labor structures, or software ecosystems as developed nations, allowing them to adopt new technologies more rapidly and efficiently. AI could enable these regions to leapfrog traditional stages of development in finance, logistics, education, commerce, and industrial productivity. The ETF focuses on companies positioned to benefit from digital adoption, platform economics, financial infrastructure growth, and rising productivity within emerging markets. The core idea is that AI may compress development timelines globally and allow certain emerging economies to modernize faster than traditional economic models would suggest.",
  "AI Induced Deflation":
    "This ETF is built around the belief that AI will become one of the largest deflationary forces in modern economic history by fundamentally lowering the cost of production across nearly every industry. As machine learning, automation, intelligent software, robotics, and AI-assisted decision-making improve, companies will increasingly be able to produce goods and services with less labor, less friction, lower operational overhead, and far greater efficiency. The core thesis is that AI does not simply create new software products — it restructures the economics of production itself.",
  "Downcycle Defensive":
    "This ETF is designed around resilience during periods of economic stress, tightening liquidity, or slowing growth. The focus is on companies with durable cash flows, defensive demand characteristics, strong balance sheets, pricing power, and relatively stable long-term demand regardless of economic cycles. These businesses may include staples, essential services, healthcare infrastructure, mission-critical software, industrial necessities, or companies with strong recurring demand dynamics. The broader thesis is that during market dislocations, liquidity contractions, or recessions, capital tends to rotate toward reliability, survivability, and cash flow stability. Rather than maximizing upside during speculative expansions, this ETF is designed to preserve durability across difficult macro environments while still maintaining long-term compounding potential.",
  "Long Shots":
    "This ETF represents the speculative and asymmetric side of the portfolio. It focuses on smaller-cap, emerging, or highly volatile companies with significant upside potential if their technologies, business models, or industries succeed over the long term. Many of these companies operate in areas such as next-generation energy, biotech, robotics, advanced manufacturing, AI infrastructure, quantum systems, or disruptive transportation technologies. The thesis is not that all of these companies will succeed, but that a small number of winners could potentially generate outsized returns that compensate for the elevated risk across the basket. This ETF is intentionally designed to capture optionality, convexity, and technological upside while accepting substantially higher volatility and uncertainty.",
};

function normalizeEtfName(value) {
  return String(value || "").trim();
}

function getEtfSortRank(name) {
  const normalized = normalizeEtfName(name);
  const idx = ETF_ORDER.findIndex((x) => x.toLowerCase() === normalized.toLowerCase());
  return idx === -1 ? 999 : idx;
}

function getEtfDescription(name) {
  const normalized = normalizeEtfName(name);
  const key = ETF_ORDER.find((x) => x.toLowerCase() === normalized.toLowerCase());
  return ETF_DESCRIPTIONS[key] || "";
}


function useIsMobile(breakpoint = 900) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}



const columns = [
  { label: "", key: "logo", width: 38 },
  { label: "Ticker", key: "ticker", width: 82 },
  { label: "Company", key: "name", width: 230 },
  { label: "ETF Weight", key: "allocation", width: 150, align: "right" },
  { label: "Price", key: "price", width: 102, align: "right" },
  { label: "Market Cap", key: "marketCap", width: 120, align: "right" },
  { label: "Beta", key: "beta", width: 80, align: "right" },
  { label: "RSI", key: "rsi14", width: 80, align: "right" },
  { label: "Forward P/E", key: "forwardPE", width: 100, align: "right" },
  { label: "P/B", key: "pb", width: 80, align: "right" },
  { label: "Growth Rate", key: "growthRate", width: 90, align: "right" },
];


const mobileColumns = columns
  .filter((col) => ["ticker", "allocation", "price", "marketCap", "rsi14", "beta"].includes(col.key))
  .map((col) => {
    if (col.key === "ticker") {
      return {
        ...col,
        sticky: false,
        left: undefined,
        width: 76,
      };
    }

    return {
      ...col,
      sticky: false,
      left: undefined,
      width:
        col.key === "allocation" ? 106 :
        col.key === "price" ? 78 :
        col.key === "marketCap" ? 96 :
        col.key === "rsi14" ? 58 :
        col.key === "beta" ? 58 :
        col.width,
    };
  });

function parseNumber(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;
  const cleaned = s.replace(/[$,%]/g, "").replace(/,/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function allocationToPct(v) {
  const n = parseNumber(v);
  if (!Number.isFinite(n)) return null;
  return Math.abs(n) <= 1 ? n * 100 : n;
}

function formatPercent(v, decimals = 2) {
  const num = parseNumber(v);
  if (!Number.isFinite(num)) return "—";
  const pct = Math.abs(num) <= 1 ? num * 100 : num;
  return pct.toFixed(decimals) + "%";
}

function formatMoney(v) {
  const n = parseNumber(v);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatLargeMoney(n) {
  const value = Number(n);
  if (!Number.isFinite(value)) return "—";
  const abs = Math.abs(value);

  if (abs >= 1_000_000_000_000) return `$${(value / 1_000_000_000_000).toFixed(2)}T`;
  if (abs >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${(value / 1_000).toFixed(2)}K`;

  return `$${value.toFixed(0)}`;
}

function formatRatio(v, decimals = 2) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(decimals);
}

function getInitials(nameOrTicker) {
  const s = String(nameOrTicker || "").trim();
  if (!s) return "?";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function CompanyLogo({ ticker, name }) {
  const [failed, setFailed] = useState(false);
  const cleanTicker = String(ticker || "").trim().toUpperCase();
  const logoUrl = cleanTicker
    ? `https://financialmodelingprep.com/image-stock/${encodeURIComponent(cleanTicker)}.png`
    : "";

  if (!logoUrl || failed) {
    return (
      <div
        style={{
          width: 24,
          height: 24,
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #e0f2fe 0%, #eef2ff 100%)",
          color: "#1e40af",
          fontSize: 11,
          fontWeight: 900,
          border: "1px solid #dbeafe",
        }}
      >
        {getInitials(cleanTicker || name)}
      </div>
    );
  }

  return (
    <img
      src={logoUrl}
      alt=""
      width={24}
      height={24}
      style={{
        borderRadius: 8,
        display: "block",
        objectFit: "contain",
        background: "#fff",
        border: "1px solid #e2e8f0",
        padding: 4,
        boxShadow: "0 4px 10px rgba(15,23,42,0.08)",
      }}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

function AllocationBar({ value, maxValue, compact = false }) {
  const pct = allocationToPct(value);
  const safeMax = Number.isFinite(maxValue) && maxValue > 0 ? maxValue : 100;
  const width = Number.isFinite(pct) ? Math.max(2, Math.min(100, (pct / safeMax) * 100)) : 0;

  return (
    <div style={{ display: "grid", gap: 6, minWidth: compact ? 88 : 120 }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <span style={{ fontSize: 11, fontWeight: 900, color: "#0f172a" }}>
          {Number.isFinite(pct) ? `${pct.toFixed(2)}%` : "—"}
        </span>
      </div>
      <div
        style={{
          height: 6,
          width: "100%",
          background: "#e2e8f0",
          borderRadius: 999,
          overflow: "hidden",
          boxShadow: "inset 0 1px 2px rgba(15,23,42,0.08)",
        }}
      >
        <div
          style={{
            width: `${width}%`,
            height: "100%",
            borderRadius: 999,
            background: "linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)",
          }}
        />
      </div>
    </div>
  );
}

function TabButton({ active, children, onClick, fullWidth = false }) {
  return (
    <button
      onClick={onClick}
      style={{
        border: active ? "1px solid #2563eb" : "1px solid #e2e8f0",
        background: active ? "#eff6ff" : "#ffffff",
        color: active ? "#1d4ed8" : "#334155",
        borderRadius: 999,
        padding: "8px 12px",
        fontSize: 12,
        fontWeight: 900,
        cursor: "pointer",
        whiteSpace: "normal",
        width: fullWidth ? "100%" : "auto",
        minHeight: fullWidth ? 42 : "auto",
        textAlign: "center",
        boxShadow: active ? "0 8px 18px rgba(37,99,235,0.12)" : "none",
      }}
    >
      {children}
    </button>
  );
}

function HeroStat({ label, value }) {
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 16,
        background: "rgba(255,255,255,0.1)",
        border: "1px solid rgba(255,255,255,0.16)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
      }}
    >
      <div
        style={{
          fontSize: 10,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "#cbd5e1",
          fontWeight: 800,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 950, letterSpacing: "-0.04em" }}>
        {value}
      </div>
    </div>
  );
}

export default function CustomETFs() {
  const isMobile = useIsMobile(900);

  const [customEtfs, setCustomEtfs] = useState([]);
  const [screenerMap, setScreenerMap] = useState({});
  const [activeEtf, setActiveEtf] = useState("");
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("allocation");
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const TH_PAD = isMobile ? "7px 4px" : "8px 8px";
  const TD_PAD = isMobile ? "7px 4px" : "8px 8px";
  const HEADER_FONT = isMobile ? 8 : 10;
  const CELL_FONT = isMobile ? 10 : 11;

  const LOGO_W = isMobile ? 38 : 42;
  const TICKER_W = isMobile ? 78 : 84;

  const visibleColumns = isMobile ? mobileColumns : columns;

  useEffect(() => {
    let cancelled = false;

    async function loadCustomEtfs() {
      try {
        setLoading(true);
        setError("");

        const base = process.env.REACT_APP_SHEETS_API_URL;
        if (!base) throw new Error("Missing REACT_APP_SHEETS_API_URL (.env + restart)");

        const apiKey = process.env.REACT_APP_API_KEY;
        if (!apiKey) throw new Error("Missing REACT_APP_API_KEY (.env + restart)");

        const url = `${base}?type=custometf&key=${encodeURIComponent(apiKey)}`;
        const res = await fetchWithLocalCache(url, "custom_etf_list_v1", 60 * 60 * 1000);
        const payload = res?.data ?? res;
        const data = (Array.isArray(payload) ? payload : [])
          .map((etf) => ({
            ...etf,
            ETF: normalizeEtfName(etf?.ETF),
          }))
          .sort((a, b) => {
            const ar = getEtfSortRank(a?.ETF);
            const br = getEtfSortRank(b?.ETF);
            if (ar !== br) return ar - br;
            return String(a?.ETF || "").localeCompare(String(b?.ETF || ""));
          });

        if (!cancelled) {
          setCustomEtfs(data);
          setActiveEtf((prev) => prev || data?.[0]?.ETF || "");
        }
      } catch (e) {
        if (!cancelled) setError(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadCustomEtfs();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadScreenerData() {
      try {
        const res = await fetch(STOCK_SCREENER_URL, { cache: "no-store" });
        const text = await res.text();

        if (!res.ok || text.trim().startsWith("<")) return;

        const json = JSON.parse(text);
        const data = Array.isArray(json?.data) ? json.data : [];

        const map = {};
        for (const row of data) {
          const ticker = String(row?.ticker || "").trim().toUpperCase();
          if (ticker) map[ticker] = row;
        }

        if (!cancelled) setScreenerMap(map);
      } catch (err) {
        console.error("Unable to load stock screener data", err);
      }
    }

    loadScreenerData();

    return () => {
      cancelled = true;
    };
  }, []);

  const activeGroup = useMemo(() => {
    return customEtfs.find((x) => String(x.ETF) === String(activeEtf)) || customEtfs[0] || null;
  }, [customEtfs, activeEtf]);

  const activeDescription = useMemo(() => {
    return getEtfDescription(activeGroup?.ETF || activeEtf);
  }, [activeGroup, activeEtf]);

  const enrichedHoldings = useMemo(() => {
    const holdings = Array.isArray(activeGroup?.Holdings) ? activeGroup.Holdings : [];

    return holdings.map((h, index) => {
      const ticker = String(h?.Ticker || "").trim().toUpperCase();
      const screener = screenerMap[ticker] || {};
      const allocation = allocationToPct(h?.AllocationOfETF);

      return {
        index,
        ticker,
        name: String(h?.Name || screener?.name || screener?.stock || "").trim(),
        allocation,
        price: parseNumber(screener?.price),
        marketCap: parseNumber(screener?.marketCap),
        beta: parseNumber(screener?.beta),
        rsi14: parseNumber(screener?.rsi14 ?? screener?.rsi ?? screener?.RSI),
        forwardPE: parseNumber(screener?.forwardPE),
        pb: parseNumber(screener?.pb),
        growthRate: parseNumber(screener?.growthRate),
      };
    });
  }, [activeGroup, screenerMap]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return enrichedHoldings;

    return enrichedHoldings.filter((h) =>
      [h.ticker, h.name].join(" ").toLowerCase().includes(q)
    );
  }, [enrichedHoldings, query]);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;

    return [...filtered].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];

      if (Number.isFinite(av) && !Number.isFinite(bv)) return -1;
      if (!Number.isFinite(av) && Number.isFinite(bv)) return 1;
      if (Number.isFinite(av) && Number.isFinite(bv)) return (av - bv) * dir;

      const as = String(av || "").toLowerCase();
      const bs = String(bv || "").toLowerCase();
      if (as < bs) return -1 * dir;
      if (as > bs) return 1 * dir;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  const stats = useMemo(() => {
    const allocations = enrichedHoldings
      .map((x) => x.allocation)
      .filter(Number.isFinite);

    const totalAllocation = allocations.reduce((sum, x) => sum + x, 0);
    const maxAllocation = allocations.length ? Math.max(...allocations) : 0;

    const topHolding = [...enrichedHoldings]
      .filter((x) => Number.isFinite(x.allocation))
      .sort((a, b) => b.allocation - a.allocation)[0];

    let betaNumerator = 0;
    let betaDenom = 0;
    let rsiNumerator = 0;
    let rsiDenom = 0;
    let capNumerator = 0;
    let capDenom = 0;

    for (const h of enrichedHoldings) {
      if (Number.isFinite(h.allocation) && Number.isFinite(h.beta)) {
        betaNumerator += h.allocation * h.beta;
        betaDenom += h.allocation;
      }

      if (Number.isFinite(h.allocation) && Number.isFinite(h.rsi14)) {
        rsiNumerator += h.allocation * h.rsi14;
        rsiDenom += h.allocation;
      }

      if (Number.isFinite(h.allocation) && Number.isFinite(h.marketCap)) {
        capNumerator += h.allocation * h.marketCap;
        capDenom += h.allocation;
      }
    }

    return {
      totalAllocation,
      maxAllocation,
      topHolding,
      weightedBeta: betaDenom > 0 ? betaNumerator / betaDenom : null,
      weightedRSI: rsiDenom > 0 ? rsiNumerator / rsiDenom : null,
      weightedMarketCap: capDenom > 0 ? capNumerator / capDenom : null,
      count: enrichedHoldings.length,
    };
  }, [enrichedHoldings]);

  function handleSort(colKey) {
    if (colKey === "logo") return;

    if (sortKey === colKey) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(colKey);
      setSortDir(colKey === "name" || colKey === "ticker" ? "asc" : "desc");
    }
  }

  function renderCell(row, col) {
    if (col.key === "logo") return <CompanyLogo ticker={row.ticker} name={row.name} />;

    if (col.key === "ticker") {
      return row.ticker ? (
        <Link
          to={`/stocks2/${encodeURIComponent(row.ticker)}`}
          style={{ textDecoration: "none", color: "#2563eb", fontWeight: 950 }}
        >
          {row.ticker}
        </Link>
      ) : "—";
    }

    if (col.key === "name") return row.name || "—";
    if (col.key === "allocation") {
      return <AllocationBar value={row.allocation} maxValue={stats.maxAllocation} compact={col.width <= 106} />;
    }
    if (col.key === "price") return Number.isFinite(row.price) ? formatMoney(row.price) : "—";
    if (col.key === "marketCap") return Number.isFinite(row.marketCap) ? formatLargeMoney(row.marketCap) : "—";
    if (col.key === "beta") return formatRatio(row.beta, 2);
    if (col.key === "rsi14") return formatRatio(row.rsi14, 1);
    if (col.key === "forwardPE") return formatRatio(row.forwardPE, 2);
    if (col.key === "pb") return formatRatio(row.pb, 2);
    if (col.key === "growthRate") return Number.isFinite(row.growthRate) ? formatPercent(row.growthRate, 2) : "—";
    return row[col.key] ?? "—";
  }

  const stickyLeftBase = { position: "sticky", zIndex: 4, background: "#fff" };

  const stickyLogo = {
    ...stickyLeftBase,
    left: 0,
    width: LOGO_W,
    minWidth: LOGO_W,
    maxWidth: LOGO_W,
    boxShadow: "1px 0 0 rgba(226,232,240,0.9)",
  };

  const stickyTicker = {
    ...stickyLeftBase,
    left: LOGO_W,
    width: TICKER_W,
    minWidth: TICKER_W,
    maxWidth: TICKER_W,
    boxShadow: "2px 0 0 rgba(226,232,240,0.9)",
  };

  const stickyHeaderBase = { position: "sticky", top: 0, zIndex: 7, background: "#f8fafc" };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
        padding: isMobile ? 12 : 24,
        fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
        color: "#0f172a",
      }}
    >
      <div style={{ maxWidth: 1280, width: "100%", minWidth: 0, margin: "0 auto" }}>
        <section
          style={{
            borderRadius: 26,
            padding: isMobile ? 16 : 22,
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #334155 100%)",
            color: "white",
            boxShadow: "0 24px 60px rgba(15,23,42,0.22)",
            marginBottom: 18,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 900, color: "#93c5fd", letterSpacing: "0.13em", textTransform: "uppercase", marginBottom: 8 }}>
            </div>
            <h1 style={{ margin: 0, fontSize: isMobile ? 26 : 36, lineHeight: 1, letterSpacing: "-0.055em" }}>
              Allan Porter Custom ETFs
            </h1>
            <p style={{ margin: "10px 0 0", maxWidth: 760, color: "#cbd5e1", fontSize: 14, lineHeight: 1.6 }}>
            </p>

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(auto-fit, minmax(45px, 1fr))", gap: 5, marginTop: 9 }}>
              <HeroStat label="Holdings" value={stats.count} />
              <HeroStat label="Top Holding" value={stats.topHolding?.ticker || "—"} />
              <HeroStat label="Weighted Beta" value={Number.isFinite(stats.weightedBeta) ? stats.weightedBeta.toFixed(2) : "—"} />
              <HeroStat label="Weighted RSI" value={Number.isFinite(stats.weightedRSI) ? stats.weightedRSI.toFixed(1) : "—"} />
            </div>
          </div>
        </section>

        <section
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: 22,
            background: "rgba(255,255,255,0.86)",
            boxShadow: "0 16px 40px rgba(15,23,42,0.1)",
            overflow: "hidden",
          }}
        >
          <div style={{ padding: isMobile ? 14 : 18, borderBottom: "1px solid #e2e8f0", background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 900, letterSpacing: "-0.03em" }}>
                  {activeGroup?.ETF || "Custom ETF"} Holdings
                </div>
                <div style={{ color: "#64748b", fontSize: 13, marginTop: 3 }}>
                  {isMobile
                    ? ""
                    : activeDescription}
                </div>
              </div>

              <div style={{ position: "relative", width: isMobile ? "100%" : 340 }}>
{/*                <span style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 14 }}>
                  ⌕
                </span>
*/}{/*                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search ticker or company..."
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "11px 14px 11px 34px",
                    minWidth: 0,
                    fontSize: 13,
                    border: "1px solid #cbd5e1",
                    borderRadius: 14,
                    outline: "none",
                    background: "white",
                    boxShadow: "0 1px 2px rgba(15,23,42,0.05)",
                  }}
                />
*/}              </div>
            </div>

            <div
              style={{
                display: isMobile ? "grid" : "flex",
                gridTemplateColumns: isMobile ? "repeat(3, minmax(0, 1fr))" : undefined,
                gap: 8,
                overflowX: isMobile ? "visible" : "auto",
                paddingBottom: 2,
                WebkitOverflowScrolling: "touch",
              }}
            >
              {customEtfs.map((etf) => (
                <TabButton
                  key={etf.ETF}
                  active={String(activeEtf) === String(etf.ETF)}
                  fullWidth={isMobile}
                  onClick={() => {
                    setActiveEtf(etf.ETF);
                    setQuery("");
                  }}
                >
                  {etf.ETF}
                </TabButton>
              ))}
            </div>
          </div>

          {loading && <div style={{ padding: 18, color: "#64748b" }}>Loading custom ETFs…</div>}
          {error && <div style={{ margin: 16, padding: 14, color: "#9f1239", background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 14 }}>Error: {error}</div>}

          {!loading && !error && (
            <div
              style={{
                overflowX: isMobile ? "hidden" : "auto",
                overflowY: "auto",
                maxHeight: "72vh",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <table
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: isMobile ? 0 : 1120,
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  tableLayout: "fixed",
                }}
              >
                <thead>
                  <tr>
                    {visibleColumns.map((c) => {
                      const isLogo = c.key === "logo";
                      const isTicker = c.key === "ticker";
                      const isActive = sortKey === c.key;

                      return (
                        <th
                          key={c.key}
                          onClick={() => handleSort(c.key)}
                          style={{
                            padding: TH_PAD,
                            fontSize: HEADER_FONT,
                            fontWeight: 900,
                            color: isActive ? "#1d4ed8" : "#64748b",
                            cursor: c.key === "logo" ? "default" : "pointer",
                            userSelect: "none",
                            borderBottom: "1px solid #e2e8f0",
                            textAlign: c.align || "left",
                            whiteSpace: "nowrap",
                            letterSpacing: "0.05em",
                            textTransform: "uppercase",
                            width: isMobile ? undefined : c.width,
                            minWidth: isMobile ? 0 : c.width,
                            maxWidth: isMobile ? undefined : c.width,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            ...(stickyHeaderBase || {}),
                            ...(isLogo && c.sticky ? { ...stickyLogo, ...stickyHeaderBase, zIndex: 11 } : {}),
                            ...(isTicker && c.sticky ? { ...stickyTicker, ...stickyHeaderBase, zIndex: 11 } : {}),
                          }}
                        >
                          {c.label}
                          {isActive ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody>
                  {sorted.length === 0 ? (
                    <tr>
                      <td colSpan={visibleColumns.length} style={{ padding: 24, color: "#64748b", textAlign: "center" }}>
                        No holdings found for this custom ETF.
                      </td>
                    </tr>
                  ) : (
                    sorted.map((row, displayIdx) => {
                      const rowBg = displayIdx % 2 === 0 ? "#ffffff" : "#fbfdff";

                      return (
                        <tr key={`${row.ticker || "row"}-${row.index}`} style={{ background: rowBg }}>
                          {visibleColumns.map((col) => {
                            const isLogo = col.key === "logo";
                            const isTicker = col.key === "ticker";

                            return (
                              <td
                                key={col.key}
                                style={{
                                  padding: TD_PAD,
                                  fontSize: CELL_FONT,
                                  borderBottom: "1px solid #eef2f7",
                                  whiteSpace: col.key === "name" ? "normal" : "nowrap",
                                  overflowWrap: col.key === "name" ? "break-word" : undefined,
                                  lineHeight: col.key === "name" ? 1.2 : undefined,
                                  textAlign: col.align || "left",
                                  fontVariantNumeric: col.align === "right" ? "tabular-nums" : undefined,
                                  width: isMobile ? undefined : col.width,
                                  minWidth: isMobile ? 0 : col.width,
                                  maxWidth: isMobile ? undefined : col.width,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  ...(isLogo && col.sticky ? { ...stickyLogo, background: rowBg } : {}),
                                  ...(isTicker && col.sticky ? { ...stickyTicker, background: rowBg } : {}),
                                }}
                              >
                                {renderCell(row, col)}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
